import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/EcoAppsAuthContext';
import { getDemoProducts, getLogoutRedirectPath, isDemoUser } from '../../services/demoSession';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Wallet,
  FileBarChart,
  Settings,
  LogOut,
  X,
  Users,
  CalendarDays,
  BriefcaseBusiness,
  Clock3,
  Activity,
  Send,
  Calculator,
  ShieldCheck,
  Brain,
  Bell,
  FolderTree,
} from 'lucide-react';

/**
 * Sidebar Component - Main navigation sidebar
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Sidebar open state (mobile)
 * @param {function} props.onClose - Close sidebar callback
 * @param {boolean} props.collapsed - Collapsed state (desktop)
 * @param {function} props.onToggleCollapse - Toggle collapse callback
 */
const Sidebar = ({ 
  open, 
  onClose, 
  collapsed = false, 
  onToggleCollapse 
}) => {
  const { user, logout, setActiveRentalBizMode } = useAuth();
  const location = useLocation();
  const [hasAgendaLabLicense, setHasAgendaLabLicense] = useState(false);
  const [hasMaterialCalculatorLicense, setHasMaterialCalculatorLicense] = useState(false);
  const [hasRentalTechLicense, setHasRentalTechLicense] = useState(false);
  const [hasBrainEmprendedorLicense, setHasBrainEmprendedorLicense] = useState(false);
  const [hasEntrepreneurCatalogLicense, setHasEntrepreneurCatalogLicense] = useState(false);
  const isAgendaLabModule = location.pathname.startsWith('/agendalab');
  const isMaterialCalculatorModule = location.pathname.startsWith('/material-calculator');
  const isRentalTechModule = location.pathname.startsWith('/rentaltech');
  const isBrainEmprendedorModule = location.pathname.startsWith('/brain-emprendedor');
  const isEntrepreneurCatalogModule = location.pathname.startsWith('/entrepreneurcatalog');
  const isAdminModule = location.pathname.startsWith('/admin');
  const roles = user?.Roles || [];
  const normalizedRoles = roles.map((role) => String(role || '').trim().toLowerCase());
  const isAdminUser = normalizedRoles.includes('admin') || normalizedRoles.includes('superadmin');
  const hasAgendaLabProduct = (products) =>
    (products || []).some((p) => p.slug === 'appointments' || p.slug === 'agendalab');

  useEffect(() => {
    if (!user) return undefined;

    let isMounted = true;
    const loadProducts = async () => {
      if (isDemoUser(user)) {
        const products = getDemoProducts();
        if (isMounted) {
          setHasAgendaLabLicense(hasAgendaLabProduct(products));
          setHasMaterialCalculatorLicense((products || []).some((p) => p.slug === 'material-calculator'));
          setHasRentalTechLicense((products || []).some((p) => p.slug === 'rentaltech'));
          setHasBrainEmprendedorLicense((products || []).some((p) => p.slug === 'brain-emprendedor'));
          setHasEntrepreneurCatalogLicense((products || []).some((p) => p.slug === 'entrepreneurcatalog'));
        }
        return;
      }

      try {
        const response = await fetch('/api/user/products', { credentials: 'include' });
        if (!response.ok) return;
        const products = await response.json();
        if (isMounted) {
          setHasAgendaLabLicense(hasAgendaLabProduct(products));
          setHasMaterialCalculatorLicense((products || []).some((p) => p.slug === 'material-calculator'));
          setHasRentalTechLicense((products || []).some((p) => p.slug === 'rentaltech'));
          setHasBrainEmprendedorLicense((products || []).some((p) => p.slug === 'brain-emprendedor'));
          setHasEntrepreneurCatalogLicense((products || []).some((p) => p.slug === 'entrepreneurcatalog'));
        }
      } catch {
        if (isMounted) {
          setHasAgendaLabLicense(false);
          setHasMaterialCalculatorLicense(false);
          setHasRentalTechLicense(false);
          setHasBrainEmprendedorLicense(false);
          setHasEntrepreneurCatalogLicense(false);
        }
      }
    };

    loadProducts();
    return () => { isMounted = false; };
  }, [user]);

  const rentalBizNavigation = [
    {
      name: 'Productos',
      href: '/dashboard',
      icon: LayoutDashboard,
      roles: ['landlord', 'tenant']
    },
    { 
      name: 'Panel', 
      href: '/rentalbiz/dashboard', 
      icon: LayoutDashboard,
      roles: ['landlord', 'tenant']
    },
    { 
      name: 'Propiedades', 
      href: '/rentalbiz/properties', 
      icon: Building2,
      roles: ['landlord', 'tenant']
    },
    { 
      name: 'Contratos', 
      href: '/rentalbiz/contracts', 
      icon: FileText,
      roles: ['landlord', 'tenant']
    },
    {
      name: 'Arrendatarios',
      href: '/rentalbiz/arrendatarios',
      icon: Users,
      roles: ['landlord']
    },
    { 
      name: 'Pagos', 
      href: '/rentalbiz/payments', 
      icon: Wallet,
      roles: ['landlord', 'tenant']
    },
    { 
      name: 'Cuentas de cobro', 
      href: '/rentalbiz/certificados', 
      icon: FileBarChart,
      roles: ['landlord', 'tenant']
    },
    { 
      name: 'Configuración', 
      href: '/rentalbiz/configuracion', 
      icon: Settings,
      roles: ['landlord', 'tenant']
    },
    ...(hasAgendaLabLicense && !isAgendaLabModule ? [{
      name: 'Citas',
      href: '/agendalab',
      icon: CalendarDays,
      roles: ['landlord', 'tenant']
    }] : []),
    ...(hasMaterialCalculatorLicense && !isMaterialCalculatorModule ? [{
      name: 'Calculadora de materiales',
      href: '/material-calculator',
      icon: Calculator,
      roles: ['landlord', 'tenant']
    }] : []),
    ...(hasRentalTechLicense && !isRentalTechModule ? [{
      name: 'RentalTech LATAM',
      href: '/rentaltech/dashboard',
      icon: Activity,
      roles: ['landlord', 'tenant']
    }] : []),
    ...(hasBrainEmprendedorLicense && !isBrainEmprendedorModule ? [{
      name: 'BrainEmprendedor',
      href: '/brain-emprendedor/dashboard',
      icon: Brain,
      roles: ['landlord', 'tenant']
    }] : []),
    ...(hasEntrepreneurCatalogLicense && !isEntrepreneurCatalogModule ? [{
      name: 'CatalogoEmprendedor',
      href: '/entrepreneurcatalog/products',
      icon: BriefcaseBusiness,
      roles: ['landlord', 'tenant']
    }] : []),
    ...(isAdminUser && !isAdminModule ? [{
      name: 'Administración',
      href: '/admin/licenses',
      icon: ShieldCheck,
      roles: ['landlord', 'tenant']
    }] : []),
  ];

  const adminNavigation = [
    { name: 'Productos', href: '/dashboard', icon: LayoutDashboard, roles: ['landlord', 'tenant'] },
    { name: 'Licencias', href: '/admin/licenses', icon: ShieldCheck, roles: ['landlord', 'tenant'] },
    { name: 'Usuarios', href: '/admin/users', icon: Users, roles: ['landlord', 'tenant'] },
    { name: 'Esquema', href: '/admin/schema', icon: Activity, roles: ['landlord', 'tenant'] },
    ...(isAdminUser ? [
      { name: 'Categorias', href: '/admin/categories', icon: BriefcaseBusiness, roles: ['landlord', 'tenant'] },
      { name: 'Productos', href: '/admin/products', icon: Calculator, roles: ['landlord', 'tenant'] }
    ] : [])
  ];

  const materialCalculatorNavigation = [
    { name: 'Productos', href: '/dashboard', icon: LayoutDashboard, roles: ['landlord', 'tenant'] },
    { name: 'Proyectos', href: '/material-calculator/projects', icon: LayoutDashboard, roles: ['landlord', 'tenant'] },
    { name: 'Asistente', href: '/material-calculator/projects', icon: Clock3, roles: ['landlord', 'tenant'] },
    { name: 'Reglas y parámetros', href: '/material-calculator/rulesets', icon: Activity, roles: ['landlord', 'tenant'] },
    { name: 'Catálogo de materiales', href: '/material-calculator/catalog', icon: BriefcaseBusiness, roles: ['landlord', 'tenant'] },
    { name: 'Reportes', href: '/material-calculator/projects', icon: FileBarChart, roles: ['landlord', 'tenant'] },
    { name: 'Configuración', href: '/material-calculator/settings', icon: Settings, roles: ['landlord', 'tenant'] },
    ...(isAdminUser ? [{
      name: 'Administración',
      href: '/admin/licenses',
      icon: ShieldCheck,
      roles: ['landlord', 'tenant']
    }] : []),
  ];

  const rentalTechNavigation = [
    { name: 'Productos', href: '/dashboard', icon: LayoutDashboard, roles: ['landlord', 'tenant'] },
    { name: 'Panel', href: '/rentaltech/dashboard', icon: LayoutDashboard, roles: ['landlord', 'tenant'] },
    { name: 'Equipos', href: '/rentaltech/equipments', icon: Building2, roles: ['landlord', 'tenant'] },
    { name: 'Clientes', href: '/rentaltech/customers', icon: Users, roles: ['landlord', 'tenant'] },
    { name: 'Disponibilidad', href: '/rentaltech/availability', icon: CalendarDays, roles: ['landlord', 'tenant'] },
    { name: 'Arrendamientos', href: '/rentaltech/orders', icon: FileText, roles: ['landlord', 'tenant'] },
    { name: 'Mantenimiento', href: '/rentaltech/maintenance', icon: Settings, roles: ['landlord', 'tenant'] },
    { name: 'Telemetria', href: '/rentaltech/telemetry', icon: Activity, roles: ['landlord', 'tenant'] },
    { name: 'Facturacion LATAM', href: '/rentaltech/billing', icon: Wallet, roles: ['landlord', 'tenant'] },
    { name: 'Operación móvil', href: '/rentaltech/mobile-ops', icon: Send, roles: ['landlord', 'tenant'] },
    ...(isAdminUser ? [{
      name: 'Administración',
      href: '/admin/licenses',
      icon: ShieldCheck,
      roles: ['landlord', 'tenant']
    }] : []),
  ];

  const agendaLabNavigation = [
    {
      name: 'Productos',
      href: '/dashboard',
      icon: LayoutDashboard,
      roles: ['landlord', 'tenant']
    },
    {
      name: 'Panel',
      href: '/agendalab',
      icon: LayoutDashboard,
      roles: ['landlord', 'tenant']
    },
    {
      name: 'Servicios',
      href: '/agendalab/services',
      icon: BriefcaseBusiness,
      roles: ['landlord', 'tenant']
    },
    {
      name: 'Programacion',
      href: '/agendalab/schedule',
      icon: Clock3,
      roles: ['landlord', 'tenant']
    },
    {
      name: 'Clientes',
      href: '/agendalab/clients',
      icon: Users,
      roles: ['landlord', 'tenant']
    },
    {
      name: 'Carga Laboral',
      href: '/agendalab/workload',
      icon: Activity,
      roles: ['landlord', 'tenant']
    },
    {
      name: 'Configuracion',
      href: '/agendalab/settings',
      icon: Settings,
      roles: ['landlord', 'tenant']
    },
    ...(isAdminUser ? [{
      name: 'Administración',
      href: '/admin/licenses',
      icon: ShieldCheck,
      roles: ['landlord', 'tenant']
    }] : []),
  ];

  const brainEmprendedorNavigation = [
    { name: 'Productos', href: '/dashboard', icon: LayoutDashboard, roles: ['landlord', 'tenant'] },
    { name: 'Panel', href: '/brain-emprendedor/dashboard', icon: Brain, roles: ['landlord', 'tenant'] },
    { name: 'Invitaciones', href: '/brain-emprendedor/invitations', icon: Bell, roles: ['landlord', 'tenant'] },
    { name: 'Sala activa', href: '/brain-emprendedor/dashboard', icon: Activity, roles: ['landlord', 'tenant'] },
    ...(isAdminUser ? [{
      name: 'Administración',
      href: '/admin/licenses',
      icon: ShieldCheck,
      roles: ['landlord', 'tenant']
    }] : []),
  ];

  const entrepreneurCatalogNavigation = [
    { name: 'Perfil', href: '/entrepreneurcatalog/configuration', icon: Settings, roles: ['landlord', 'tenant'] },
    { name: 'Categorias', href: '/entrepreneurcatalog/categories', icon: FolderTree, roles: ['landlord', 'tenant'] },
    { name: 'Catálogo', href: '/entrepreneurcatalog/products', icon: BriefcaseBusiness, roles: ['landlord', 'tenant'] },
    { name: 'Productos', href: '/dashboard', icon: LayoutDashboard, roles: ['landlord', 'tenant'] },
    ...(isAdminUser ? [{
      name: 'Administración',
      href: '/admin/licenses',
      icon: ShieldCheck,
      roles: ['landlord', 'tenant']
    }] : []),
  ];

  const moduleNavigation = isAgendaLabModule
    ? agendaLabNavigation
      : (isMaterialCalculatorModule
      ? materialCalculatorNavigation
      : (isRentalTechModule
        ? rentalTechNavigation
        : (isBrainEmprendedorModule
          ? brainEmprendedorNavigation
          : (isEntrepreneurCatalogModule
            ? entrepreneurCatalogNavigation
            : (isAdminModule ? adminNavigation : rentalBizNavigation)))));
  const isCurrentDemo = isDemoUser(user);
  const getCurrentModulePrefix = () => {
    if (isAgendaLabModule) return '/agendalab';
    if (isMaterialCalculatorModule) return '/material-calculator';
    if (isRentalTechModule) return '/rentaltech';
    if (isBrainEmprendedorModule) return '/brain-emprendedor';
    if (isEntrepreneurCatalogModule) return '/entrepreneurcatalog';
    if (isAdminModule) return '/admin';
    return '/rentalbiz';
  };
  const currentModulePrefix = getCurrentModulePrefix();
  const filteredNavigation = isCurrentDemo
    ? moduleNavigation.filter((item) => item.href.startsWith(currentModulePrefix))
    : moduleNavigation;
  const userRole = (isAgendaLabModule || isMaterialCalculatorModule || isRentalTechModule || isBrainEmprendedorModule || isEntrepreneurCatalogModule)
    ? 'Emprendedor'
    : (user?.role === 'landlord' ? 'Arrendador' : 'Arrendatario');
  const moduleTitle = isAdminModule
    ? 'Administración'
    : (isAgendaLabModule
      ? 'AgendaLab'
        : (isMaterialCalculatorModule
          ? 'Calculadora de materiales'
          : (isRentalTechModule
            ? 'RentalTech'
            : (isBrainEmprendedorModule
              ? 'BrainEmprendedor'
              : (isEntrepreneurCatalogModule ? 'CatalogoEmprendedor' : 'RentalBiz')))));
  const ModuleIcon = isAdminModule
    ? ShieldCheck
    : (isAgendaLabModule
      ? CalendarDays
      : (isMaterialCalculatorModule
        ? Calculator
        : (isRentalTechModule
          ? Activity
          : (isBrainEmprendedorModule
            ? Brain
            : (isEntrepreneurCatalogModule ? BriefcaseBusiness : Building2)))));

  const handleLogout = async () => {
    const redirectPath = getLogoutRedirectPath(user, location.pathname);
    await logout();
    window.location.href = redirectPath;
  };

  // Get user initials for avatar
  const userInitials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`;
  const userFullName = `${user?.first_name || ''} ${user?.last_name || ''}`;
  const canSwitchMode = user?.rentalBizAssignedMode === 'both' && !isEntrepreneurCatalogModule;

  return (
    <>
      {/* Mobile backdrop */}
      <div 
        className={`
          fixed inset-0 bg-gray-900/50 z-40 lg:hidden
          transition-opacity duration-300 ease-in-out
          ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50
          ${collapsed ? 'w-20' : 'w-64'}
          bg-white shadow-soft
          transform transition-all duration-300 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        aria-label="Sidebar navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo Header */}
          <div className={`
            flex items-center h-16 px-4 border-b border-gray-100
            ${collapsed ? 'justify-center' : 'justify-between'}
          `}>
            {!collapsed && (
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <ModuleIcon className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-display font-bold text-primary-600">
                  {moduleTitle}
                </span>
              </div>
            )}
            
            {/* Mobile close button */}
            <button
              className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              onClick={onClose}
              aria-label="Cerrar barra lateral"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Desktop collapse button */}
            <button
              className={`
                hidden lg:flex p-2 rounded-lg text-gray-500 hover:bg-gray-100
                transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500
                ${collapsed ? 'mx-auto' : ''}
              `}
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expandir barra lateral' : 'Contraer barra lateral'}
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            <ul className="space-y-1">
              {filteredNavigation.map((item) => (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) => `
                      nav-item group
                      ${isActive ? 'nav-item-active' : 'nav-item-inactive'}
                      ${collapsed ? 'justify-center px-3' : 'px-4'}
                    `}
                    onClick={() => onClose()}
                    title={collapsed ? item.name : undefined}
                  >
                    <item.icon className={`
                      w-5 h-5 flex-shrink-0
                      transition-colors duration-150
                    `} />
                    {!collapsed && (
                      <span className="ml-3 text-sm font-medium">
                        {item.name}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* User Section */}
          <div className="border-t border-gray-100 p-4">
            <div className={`flex items-center ${collapsed ? 'justify-center' : ''}`}>
              {/* Avatar */}
              <div className={`
                w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center
                flex-shrink-0
              `}>
                {userInitials ? (
                  <span className="text-sm font-semibold text-primary-600">
                    {userInitials}
                  </span>
                ) : (
                  <Users className="w-5 h-5 text-primary-600" />
                )}
              </div>

              {/* User Info */}
              {!collapsed && (
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {userFullName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {canSwitchMode ? `${userRole} (Ambos)` : userRole}
                  </p>
                </div>
              )}
            </div>

            {!collapsed && canSwitchMode && !isAgendaLabModule && !isMaterialCalculatorModule && !isRentalTechModule && !isBrainEmprendedorModule && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setActiveRentalBizMode('landlord')}
                  className={`px-2 py-1.5 text-xs rounded-md border transition-colors ${
                    user?.role === 'landlord'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-primary-300'
                  }`}
                >
                  Arrendador
                </button>
                <button
                  type="button"
                  onClick={() => setActiveRentalBizMode('tenant')}
                  className={`px-2 py-1.5 text-xs rounded-md border transition-colors ${
                    user?.role === 'tenant'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-primary-300'
                  }`}
                >
                  Arrendatario
                </button>
              </div>
            )}

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className={`
                mt-3 w-full flex items-center
                ${collapsed ? 'justify-center px-3' : 'px-4'}
                py-2.5 text-sm font-medium text-red-600
                rounded-lg hover:bg-red-50
                transition-colors duration-150
                focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
              `}
              title={collapsed ? 'Cerrar sesión' : undefined}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <span className="ml-3">Cerrar sesión</span>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;


